import { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from "@shadcn/components/ui/form";
import { getFieldHelperText } from "@forms/form-render/helpers/getFieldHelperText";
import { Input } from "@shadcn/components/ui/input";
import { Button } from "@shadcn/components/ui/button";
import { Alert, AlertDescription } from "@shadcn/components/ui/alert";
import { Loader2, Search, ExternalLink } from "lucide-react";
import { useIntegrationEnvironmentIdOptional } from "@forms/form-render/contexts/IntegrationEnvironmentContext";
import {
  sharedoOdsSearchApi,
  SharedoOdsDetail,
  SharedoOdsMatterSummary,
  SharedoOdsSearchHit,
} from "./api";
import { SharedoOdsSearchProps } from "./SharedoOdsSearch.types";
import { SharedoOdsSearchManualEntry } from "./SharedoOdsSearchManualEntry";

export const SharedoOdsSearch = ({
  name,
  required,
  disabled,
  helperText,
  entityType,
  checkExistingMatters = true,
  showManualEntryButton = false,
}: SharedoOdsSearchProps) => {
  const description = getFieldHelperText(helperText, required);
  const { control, setValue, watch } = useFormContext();
  const environmentId = useIntegrationEnvironmentIdOptional() ?? undefined;
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SharedoOdsSearchHit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [displayDetail, setDisplayDetail] = useState<SharedoOdsDetail | null>(null);
  const [matters, setMatters] = useState<SharedoOdsMatterSummary[]>([]);
  const [isLoadingDisplay, setIsLoadingDisplay] = useState(false);

  const found = watch(`${name}.found`) as boolean | undefined;
  const selectedOdsId = watch(`${name}.odsId`) as string | undefined;
  const manualFirstName = watch(`${name}.firstName`);
  const manualSurname = watch(`${name}.surname`);
  const manualOrganisationName = watch(`${name}.organisationName`);
  const manualEmail = watch(`${name}.email`);
  const manualPhone = watch(`${name}.phone`);

  const clearManualFields = useCallback(() => {
    setValue(`${name}.firstName`, undefined);
    setValue(`${name}.surname`, undefined);
    setValue(`${name}.organisationName`, undefined);
    setValue(`${name}.email`, undefined);
    setValue(`${name}.phone`, undefined);
  }, [name, setValue]);

  const clearAnswer = useCallback(() => {
    setValue(`${name}.found`, undefined);
    setValue(`${name}.odsId`, undefined);
    setValue(`${name}.odsType`, entityType);
    clearManualFields();
    setDisplayDetail(null);
    setMatters([]);
    setManualMode(false);
  }, [clearManualFields, entityType, name, setValue]);

  const loadDisplay = useCallback(
    async (odsId: string) => {
      setIsLoadingDisplay(true);
      setError(null);
      try {
        const detail = await sharedoOdsSearchApi.getById(odsId, entityType, environmentId);
        setDisplayDetail(detail);
        if (checkExistingMatters) {
          const mattersResponse = await sharedoOdsSearchApi.getMatters(odsId, environmentId);
          setMatters(mattersResponse.matters ?? []);
        } else {
          setMatters([]);
        }
      } catch (err: any) {
        console.error("Sharedo ODS get-by-id error:", err);
        setDisplayDetail(null);
        setMatters([]);
        setError(err?.response?.data?.error || "Failed to load ODS record details.");
      } finally {
        setIsLoadingDisplay(false);
      }
    },
    [checkExistingMatters, entityType, environmentId]
  );

  useEffect(() => {
    if (found === true && selectedOdsId) {
      void loadDisplay(selectedOdsId);
      return;
    }
    setDisplayDetail(null);
    setMatters([]);
  }, [found, selectedOdsId, loadDisplay]);

  useEffect(() => {
    if (found !== false || !showManualEntryButton) return;
    const hasManualData =
      Boolean(manualFirstName) ||
      Boolean(manualSurname) ||
      Boolean(manualOrganisationName) ||
      Boolean(manualEmail) ||
      Boolean(manualPhone);
    if (hasManualData) {
      setManualMode(true);
    }
  }, [
    found,
    showManualEntryButton,
    manualFirstName,
    manualSurname,
    manualOrganisationName,
    manualEmail,
    manualPhone,
  ]);

  const applyHit = useCallback(
    (hit: SharedoOdsSearchHit) => {
      setValue(`${name}.found`, true);
      setValue(`${name}.odsId`, hit.odsId);
      setValue(`${name}.odsType`, entityType);
      clearManualFields();
      setManualMode(false);
      setResults([]);
      setError(null);
      setHasSearched(true);
    },
    [clearManualFields, entityType, name, setValue]
  );

  const markNotFound = useCallback(() => {
    setValue(`${name}.found`, false);
    setValue(`${name}.odsId`, undefined);
    setValue(`${name}.odsType`, entityType);
    clearManualFields();
    setDisplayDetail(null);
    setMatters([]);
    setResults([]);
    setError(null);
    setManualMode(false);
  }, [clearManualFields, entityType, name, setValue]);

  const handleManualMode = () => {
    setValue(`${name}.found`, false);
    setValue(`${name}.odsId`, undefined);
    setValue(`${name}.odsType`, entityType);
    setDisplayDetail(null);
    setMatters([]);
    setManualMode(true);
    setResults([]);
    setError(null);
  };

  const performSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    setError(null);
    setHasSearched(true);
    clearAnswer();

    try {
      const response = await sharedoOdsSearchApi.search({
        query: searchQuery.trim(),
        entityType,
        environmentId,
        includeMatters: false,
      });
      setResults(response.results ?? []);
      if (!response.results?.length) {
        setError(
          showManualEntryButton
            ? "No matching records found. You can enter details manually."
            : 'No matching records found. Choose "None of these" to capture details with normal questions.'
        );
      }
    } catch (err: any) {
      console.error("Sharedo ODS search error:", err);
      setResults([]);
      setError(err?.response?.data?.error || "Failed to search ODS. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const validateAnswer = () => {
    if (found === true && selectedOdsId) return true;
    if (found === false) {
      if (showManualEntryButton && manualMode) {
        if (entityType === "organisation") {
          const org = watch(`${name}.organisationName`);
          return org && String(org).trim() ? true : "Organisation name is required";
        }
        const first = watch(`${name}.firstName`);
        const last = watch(`${name}.surname`);
        return first && last && String(first).trim() && String(last).trim()
          ? true
          : "First name and surname are required";
      }
      if (!required) return true;
      return true;
    }
    if (!required) return true;
    return showManualEntryButton
      ? "Search and select a record, or enter details manually"
      : 'Search and select a record, or choose "None of these"';
  };

  const showSearchUi = found !== true && !manualMode;
  const showNotFoundActions = hasSearched && found !== true && !isSearching && !manualMode;
  const showNoneOfThese = showNotFoundActions && !showManualEntryButton;
  const showEnterManually = showNotFoundActions && showManualEntryButton;

  const formatHitMeta = (hit: { title?: string; email?: string; phone?: string }) =>
    [hit.title, hit.email, hit.phone].filter(Boolean).join(" · ");

  return (
    <div className="space-y-4">
      <FormField
        control={control}
        name={`${name}.found`}
        rules={required ? { validate: validateAnswer } : undefined}
        render={() => <></>}
      />

      {showSearchUi ? (
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            value={searchQuery}
            disabled={disabled || isSearching}
            placeholder={entityType === "organisation" ? "Search organisations…" : "Search people…"}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void performSearch();
              }
            }}
          />
          <Button type="button" disabled={disabled || isSearching || !searchQuery.trim()} onClick={() => void performSearch()}>
            {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span className="ml-2">Search</span>
          </Button>
        </div>
      ) : null}

      {description ? <FormDescription>{description}</FormDescription> : null}

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {showSearchUi && results.length > 0 ? (
        <div className="max-h-72 divide-y overflow-y-auto rounded-md border border-border">
          {results.map((hit) => (
            <button
              key={hit.odsId}
              type="button"
              disabled={disabled}
              className="flex w-full flex-col gap-1 px-3 py-3 text-left hover:bg-muted/50"
              onClick={() => applyHit(hit)}
            >
              <span className="font-medium">{hit.displayName}</span>
              {formatHitMeta(hit) ? (
                <span className="text-xs text-muted-foreground">{formatHitMeta(hit)}</span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}

      {showNoneOfThese ? (
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={markNotFound}>
          None of these
        </Button>
      ) : null}

      {showEnterManually ? (
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={handleManualMode}>
          Enter manually
        </Button>
      ) : null}

      {found === false && !manualMode && !showManualEntryButton ? (
        <Alert>
          <AlertDescription>
            No ODS record selected. Continue to capture details with the following questions.
          </AlertDescription>
        </Alert>
      ) : null}

      {manualMode ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Manual entry</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() => {
                clearAnswer();
                setHasSearched(false);
                setResults([]);
                setError(null);
              }}
            >
              Back to search
            </Button>
          </div>
          <SharedoOdsSearchManualEntry
            name={name}
            disabled={disabled}
            entityType={entityType}
          />
        </div>
      ) : null}

      {found === true && selectedOdsId ? (
        <div className="space-y-3 rounded-md border border-border p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              {isLoadingDisplay ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading record…
                </p>
              ) : (
                <>
                  <p className="font-medium">{displayDetail?.displayName || selectedOdsId}</p>
                  {displayDetail && formatHitMeta(displayDetail) ? (
                    <p className="text-sm text-muted-foreground">{formatHitMeta(displayDetail)}</p>
                  ) : null}
                </>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => {
                clearAnswer();
                setHasSearched(false);
                setResults([]);
                setError(null);
              }}
            >
              Clear
            </Button>
          </div>
          {checkExistingMatters && !isLoadingDisplay && matters.length > 0 ? (
            <Alert variant="warning">
              <AlertDescription>
                <p className="mb-2 font-medium">This record already has open matters in Clio Operate.</p>
                <ul className="space-y-1">
                  {matters.map((matter) => (
                    <li key={matter.id}>
                      <a
                        href={matter.deepLink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-sm underline"
                      >
                        {matter.reference || matter.title || matter.id}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : checkExistingMatters && !isLoadingDisplay ? (
            <p className="text-sm text-muted-foreground">No open matters found for this record.</p>
          ) : null}
        </div>
      ) : null}

      <FormField
        control={control}
        name={`${name}.odsType`}
        render={({ field }) => (
          <FormItem className="hidden">
            <FormControl>
              <Input {...field} value={field.value ?? entityType} readOnly />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
